import { EditOutlined, ReadOutlined, UndoOutlined } from '@ant-design/icons'
import {
  Card,
  Col,
  Form,
  Image,
  Row,
  Tag,
  Input,
  Table,
  Button,
  Modal,
  notification,
  Tooltip,
  InputNumber,
} from 'antd'
import moment from 'moment'
import { useTranslation } from 'next-i18next'
import { useEffect, useMemo, useRef } from 'react'
import { useState } from 'react'
import userApi from 'src/api/userApi'
import useGetWithContext from 'src/hooks/useGetWithContext'
import { linkSeeInstructions } from 'src/libs/constant'
import { UsernameById1 } from '../common/other/UsernameById'
import PageHeader from '../common/PageHeader'
import { RateStudent } from './RateStudent'
import { useModalState } from 'src/hooks/useModalState'
import { getDateFormat } from 'src/libs/util'
import { TimeSlotColumn } from '../common/TimeSlotColumn'
import statisticReportApi from 'src/api/statisticsReport'
import { hasRole } from 'src/libs/browser'
import Icon from '@ant-design/icons/lib/components/Icon'
import { useGetData } from 'src/hooks/useGetData'
import { checkDecimalPartIsZeroOrFive } from 'src/libs/regex'
import sessionApi from 'src/api/sessionApi'
const { TextArea } = Input

export function StatisticsReportDetail({ data, handleReload, status }) {
  const { t } = useTranslation()

  return (
    <>
      <PageHeader title={t('page.statistics_report.text.statistics_report_detail')} />
      <SessionInfo data={data} />
      <SessionContent data={data} />
      {data && <SessionComment data={data} handleReload={handleReload} />}
      <HistorySession data={data} />
    </>
  )
}

export function SessionInfo({ data }) {
  const { t } = useTranslation()
  const { sessionCode, dayOfWeek, learnDate, classTimeSlotId, sessionType, teacherId } = data || {}

  return (
    <Card style={cardStyle}>
      <Row className='m-2'>
        <Col span={4}>
          <span>{t('page.statistics_report.label.code_session')}: </span>
        </Col>
        <Col>
          <Tag style={TagCss}>{sessionCode}</Tag>
        </Col>
      </Row>
      <Row className='m-2'>
        <Col span={4}>
          <span>{t('page.statistics_report.label.time_learning')}: </span>
        </Col>
        <Col>
          <Tag style={TagCss}>
            {t(`constant.weekday.${dayOfWeek}`)} - <span>{getDateFormat(learnDate)}</span>
          </Tag>{' '}
          <Tag style={{...TagCss, width: 'auto'}}>
            <TimeSlotColumn id={classTimeSlotId} />
          </Tag>
        </Col>
      </Row>
      <Row className='m-2'>
        <Col span={4}>
          <span>{t('page.statistics_report.label.session_type')}: </span>
        </Col>
        <Col>
          <Tag style={TagCss}>{t(`constant.regular_session_type.${sessionType}`)}</Tag>
        </Col>
      </Row>
      <Row className='m-2'>
        <Col span={4}>
          <span>{t('page.statistics_report.label.practical_tutor')}: </span>
        </Col>
        <Col>
          <Tag style={TagCss}>
            <NameTeacher teacherId={teacherId} />
          </Tag>
        </Col>
      </Row>
    </Card>
  )
}

export function SessionContent({ data, isDisabledTitle= false, lessonContent }) {
  const { t } = useTranslation()
  const dataLessonContent = useMemo(()=>{
    if (!!lessonContent) return lessonContent
    if (!data) return {}
    try {
      return JSON.parse(data?.lessonContent)
    } catch (error) {
      console.log(error)
      return {}
    }
  },[data, lessonContent])

  const skills = useMemo(() => {
    const { listening, readingAndWriting, speaking } =  dataLessonContent || {}
    return [
      {
        title : t(`page.statistics_report.label.readingAndWriting`) ,
        value : readingAndWriting
      }, 
      {
        title : t(`page.statistics_report.label.listening`) ,
        value : listening
      }, 
      {
        title : t(`page.statistics_report.label.speaking`) ,
        value : speaking
      }
    ].filter(item => item.value !== '')
  }, [dataLessonContent])

  return (
    <div className='mt-3'>
      {!isDisabledTitle && <span style={TitleCss}>
        {t('page.statistics_report.label.session_content')}
      </span>}
      <Card style={cardStyle}>
        <Row className='mb-1' style={{ fontSize: '17px' }}>
          <span>
            {t('page.statistics_report.label.Topic')}:&nbsp;{' '}
          </span>
          <p 
            style={{
            color: '#0876B1',
            borderColor: '#0876B1',
            borderRadius: '5px', }}
          >
            {dataLessonContent?.topic}
          </p>
        </Row>
        <Row gutter={50}>
          <Col span={12}>
            <div style={titleContentSession}>
              <Icon component={() => <img src='/image/vocabulary-icon.svg' style={{ width: '20px', height: '20px' }} />} />
              &nbsp;
              <span>{t('page.statistics_report.label.vocabulary')}</span>
              <br />
            </div>
            <Row className='p-1 mt-1'>
              {dataLessonContent?.vocab?.split(', ')?.filter(item => item)?.map((item, index) => {
                return (
                  <Col key={index} span={12} className='pl-4'>
                    {index + 1}. {item}
                  </Col>
                )
              })}
            </Row>
          </Col>
          <Col span={12}>
            <div style={titleContentSession}>
              <ReadOutlined style={{ fontSize: '20px' }} /> &nbsp;
              <span>{t('page.statistics_report.label.sample_sentences')}</span>
              <br />
            </div>
            <div className='mt-2 ml-4'>
              {dataLessonContent?.structure?.split('\n')?.filter(item => item)?.map((item, index) =>
                (
                  <p key={index} className='m-1'>
                    {index + 1}. {item}
                  </p>
                )
              )}
            </div>
          </Col>
        </Row>
        <Row gutter={50} className='my-3'>
          <Col span={12}>
            <div style={titleContentSession}>
              <Icon component={() => <img src='/image/volume-icon.svg' style={{ width: '20px', height: '20px' }} />} />
              &nbsp;
              <span>{t('page.statistics_report.label.phonetic')}</span>
              <br />
            </div>
            <div className='mt-2 ml-4'>
              {dataLessonContent?.phonic?.split('\n')?.filter(item => item)?.map((item, index) =>
                (
                  <p key={index} className='m-1'>
                    {index + 1}. {item}
                  </p>
                )
              )}
            </div>
          </Col>
          <Col span={12}>
            <div style={titleContentSession}>
              <Icon component={() => <img src='/image/headphones-icon.svg' style={{ width: '20px', height: '20px' }} />} />
              &nbsp;
              <span>{t('page.statistics_report.label.skill')}</span>
              <br />
            </div>
            <div className='mt-2 ml-4'>
              {skills && (
                skills.map((skill, index) => {
                  return <p key={index} className='m-1'> {index+1}. {skill?.title}: {skill?.value} </p>
                })
              )}
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  )
}

function SessionComment({ data, handleReload }) {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const { handleOpen, handleClose, isModalVisible } = useModalState()
  const { handleOpen: handleOpenHonor, handleClose: handleCloseHonor, isModalVisible: isModalVisibleHonor } = useModalState()
  const [editable, setEditable] = useState(data?.status === 'PROCESSED' ? false : true)
  const [isSubmitting, setSubmitting] = useState(false)
  const [note, setNote] = useState()
  const [students, setStudents] = useState()
  const [timeUpdate, setTimeUpdate] = useState()
  const ruleCanUpdateComment = hasRole(['admin', 'manager', 'employee-teacher', 'manager-teacher'])

  const { data: dataSessionAchievement } = useGetWithContext(
    `GET_CUP_EVALUATOR_BY_SESSION_ID_${data?.sessionId}`,
    sessionApi.getSessionAchievement,
    data?.sessionId,
    { isDisabled: !data?.sessionId }
  );

  const isMonthlyExam = useMemo(() => {
    return dataSessionAchievement?.some((session) => !!session.monthOfReport);
  }, [dataSessionAchievement]);

  const listStudentsEvaluate = useMemo(() => {
    return data?.students.filter(item => item.status !== "CANCELLED") || []
  }, [data])

  const requiredRules = [
    {
      required: true,
      message: t('common.text.field_required'),
    },
  ]

  const handleClickEditable = () => {
    setEditable(true)
  }
  const handleClickUndo = () => {
    setEditable(false)
  }

  const handleSubmit = (values) => {
    const { note, ...rest } = values
    setNote(note)
    const studentComments = []
    for (const [key, value] of Object.entries(rest)) {
      studentComments.push({ id: key, ...value })
    }
    setStudents(studentComments)
    handleOpen()
  }

  const handleOk = async () => {
    setSubmitting(true)
    const { status } = await statisticReportApi.updateStatistics({
      id: data?.id,
      note,
      students,
    })
    if (status === 200) {
      notification.success({
        message: t('page.statistics_report.label.update_success'),
      })
      setTimeout(() => {
        handleReload()
        handleClose()
        setEditable(!editable)
        setTimeUpdate(new Date())
        setSubmitting(false)
      }, 500)
      
    }
  }
  const handleSubmitHonor = async () => {
    const { status } = await sessionApi.honorSessionAchievement(data?.sessionId)
    if(status === 200) {
      notification.success({
        message: 'Xác nhận vinh danh thành công!'
      })
    }
    handleCloseHonor()
  }

  const { data: scoringCriteriaHomework } = useGetData(statisticReportApi.getSuggestPoint, data?.id, {isDisabled: !data?.id})

  const dataContentHomework = useMemo(() => {
    return [
      ...(data?.skillsNeedEvaluated || []),
      {
        name: t('page.statistics_report.label.acquire_knowledge'),
        field: "knowledgeNote"
      },
      {
        name: t('page.statistics_report.label.learning_attitude'),
        field: "attitudeNote"
      },
    ]
  }, [data])

  
  useEffect(() => {
    form.resetFields()
    const { students, note } = data || {}
    if (students) {
      const dataComment = students.map((student) => {
        return {
          [student.id] : {
            ['knowledgeNote']: student.knowledgeNote,
            ['attitudeNote']: student.attitudeNote,
            ['vocab']: student?.vocab,
            ['phonic']: student?.phonic,
            ['structure']: student?.structure,
            ['readingAndWriting']: student?.readingAndWriting,
            ['listening']: student?.listening,
            ['speaking']: student?.speaking,
          }
        }
      })
      const evaluateSessionStudents = dataComment.reduce((total, evaluate) => {
        return {
          ...total,
          ...evaluate,
        }
      }, {})
      const values = {
        note,
        ...evaluateSessionStudents,
      }
      form.setFieldsValue(values)
    }
  }, [data, timeUpdate, editable])

  return (
    <div className='mt-3'>
      <Row style={{ margin: 'auto 40px' }} className='d-flex justify-content-end align-items-center'>
        <a href={linkSeeInstructions} target='_blank' className='mr-4 color-main'>{t('page.statistics_report.label.see_instructions')}</a>
        {ruleCanUpdateComment && (
          <>
            {editable ? (
              <Button
                className='text-secondary d-flex justify-content-center align-items-center border-0'
                icon={<UndoOutlined style={{ color: '#ff1919' }} />}
                onClick={handleClickUndo}
                style={{ fontSize: 16 }}
                shape='circle'
                ghost
              />
            ) : (
              <Tooltip title={t('page.statistics_report.label.edit_evaluate')}>
                <Button
                  className='text-secondary d-flex justify-content-center align-items-center border-0'
                  icon={<EditOutlined style={{ color: '#ff1919' }} />}
                  onClick={handleClickEditable}
                  style={{ fontSize: 16 }}
                  shape='circle'
                  ghost
                />
              </Tooltip> 
            )}
          </>
        )}
      </Row>

      <Form form={form} onFinish={handleSubmit} initialValues={{}}>
        <Row>
          <span style={TitleCss}>
            {t('page.statistics_report.label.comment_session')}{' '}
            <span style={{ color: 'red' }}>*</span>
          </span>
          <div
            style={{
              width: '95%',
              margin: 'auto',
              borderRadius: '10px',
              fontSize: '16px',
              padding: '0px',
            }}
          >
            <Row>
              <Form.Item name='note' style={{ width: '100%' }} rules={requiredRules}>
                <TextArea
                  rows={4}
                  disabled={!editable|| !ruleCanUpdateComment}
                  maxLength={2000}
                  placeholder={`1. ${t(
                    'page.statistics_report.placeholder.session_comment',
                  )}\n2. ${t('page.statistics_report.placeholder.session_advice')}`}
                  style={{
                    borderRadius: '10px',
                  }}
                />
              </Form.Item>
            </Row>
          </div>
        </Row>
        <Row style={{display: 'block'}}>
          <span style={TitleCss}>
            {t('page.statistics_report.label.comment_student')}{' '}
            <span style={{ color: 'red' }}>*</span>
          </span>
        </Row>

        <div
          style={{
            width: '95%',
            margin: 'auto',
            borderRadius: '10px',
            fontSize: '16px',
            padding: '15px',
            background: 'white'
          }}
        >
          <table style={tableLessonComment}>
            <tr>
              <th style={{...cssBorder, width: `${100/(listStudentsEvaluate.length + 1)}%`}}>{t('page.statistics_report.label.evaluation_criteria_and_student')}</th>
              {
                listStudentsEvaluate.map(student => {
                  return (
                    <th style={{...cssBorder, width: `${100/(listStudentsEvaluate.length + 1)}%`}}>
                      <UsernameById1 id={student?.studentId} useHook={useGetWithContext} fullName /><br/>
                      <span>{t('common.label.student_id')}: {student?.studentId}</span>
                    </th>
                  )
                })
              }
            </tr>
            <tr>
              <td style={{...cssBorder, width: `${100/(listStudentsEvaluate.length + 1)}%`}}>{t('page.statistics_report.label.star_and_cup')}</td>
              {
                listStudentsEvaluate.map(student => {
                  return (
                    <td style={{...cssBorder, width: `${100/(listStudentsEvaluate.length + 1)}%`}}>
                      <div className='d-flex justify-content-center'>
                        <RateStudent studentId={student?.studentId} code={data?.sessionCode} />
                      </div>
                    </td>
                  )
                })
              }
            </tr>
            {
              dataContentHomework.map((item, index) => {
                return (
                  <tr gutter={20} justify="space-between">
                    <td style={{...cssBorder, width: `${100/(listStudentsEvaluate.length + 1)}%`}}>{`${index+1}. ${item.name}`}</td>
                    {
                      listStudentsEvaluate.map(student => {
                        if(item.field === 'knowledgeNote' || item.field === 'attitudeNote') {
                          return (
                            <td style={{...cssBorder, width: `${100/(listStudentsEvaluate.length + 1)}%`}}>
                              <InputCommentStudent
                                student={student}
                                item={item}
                                type={'text'}
                                disabled={!editable|| !ruleCanUpdateComment}
                                scoringCriteriaHomework={scoringCriteriaHomework}
                              />
                            </td>
                          )
                        }
                        return (
                          <td style={{...cssBorder, width: `${100/(listStudentsEvaluate.length + 1)}%`}}>
                            <InputCommentStudent
                              student={student}
                              item={item}
                              disabled={!editable|| !ruleCanUpdateComment}
                              scoringCriteriaHomework={scoringCriteriaHomework}
                            />
                          </td>
                        )
                      })
                    }
                  </tr>
                )
              })
            }
          </table>
        </div>

        <Row className='d-lex justify-content-center mt-3'>
          {/* {
            isMonthlyExam && (
              <Button
                onClick={() => {handleOpenHonor()}}
                type='primary'
                className='mr-2'
              >
                Xác nhận vinh danh
              </Button>
            )
          } */}
          {editable && ruleCanUpdateComment && (
            <Button type='primary' htmlType='submit'>
              {t('page.statistics_report.label.save_evaluate')}
            </Button>
          )}
        </Row>
      </Form>
      <Modal
        visible={isModalVisible}
        title={t('page.statistics_report.label.save_evaluate')}
        onCancel={handleClose}
        onOk={handleOk}
        confirmLoading={isSubmitting}
        cancelText={t('common.label.cancel')}
        okText={t('common.label.save')}
        destroyOnClose
      >
        <span style={{ fontSize: '16px' }}>{t('page.statistics_report.label.confirm_save')}</span>
      </Modal>
      <Modal
        visible={isModalVisibleHonor}
        title={t("page.user.label.confirm_honors")}
        destroyOnClose
        footer={null}
        onCancel={handleCloseHonor}
      >
        Bạn có chắc muốn xác nhận vinh danh!
        <Row justify="center" className="mt-3">
          <Button className="mr-2" type="primary" onClick={handleSubmitHonor}>
            {t("page.user.label.agree_honors")}
          </Button>
          <Button onClick={handleCloseHonor}>
            {t("common.label.cancel")}
          </Button>
        </Row>
      </Modal>
    </div>
  )
}

function InputCommentStudent ({scoringCriteriaHomework, student, item, type, disabled }) {
  const { t } = useTranslation()
  const [showTooltip, setShowTooltip] = useState(false)
  const [suggestPoint, setSuggestPoint] = useState('')

  const handleChangeInputPoint = async (value) => {
    const scoreHomework = scoringCriteriaHomework.find((scoreHomework) => {
      return scoreHomework.min <= value && scoreHomework.max >= value && item?.code === scoreHomework?.type
    })
    if (scoreHomework && value) {
      setShowTooltip(true)
      setSuggestPoint(scoreHomework.content)
    } else {
      setShowTooltip(false)
      setSuggestPoint('')
    }
  }

  const requiredRules = [
    {
      required: true,
      message: t('common.text.field_required')
    },
  ]

  if(type === 'text') {
    return (
      <>
        <Form.Item name={[student?.id, item?.field]} rules={requiredRules}>
          <TextArea 
            rows={3}
            disabled={disabled}
            maxLength={1000}
            placeholder={t('page.statistics_report.placeholder.enter_comment')}
            style={{ borderRadius: '10px', width: '90%', margin: '20px auto 0 auto' }}
          />
        </Form.Item>
      </>
    )
  }

  return <>
    <Tooltip
      overlayStyle={{ maxWidth: 400, whiteSpace: 'pre-line' }}
      placement='bottom'
      title={suggestPoint}
      visible={showTooltip}
    >
      <Form.Item 
        name={[student?.id, item?.field]}
        rules={[
          ...requiredRules, 
          {
            validator: (_, value) => {
              if (!value || checkDecimalPartIsZeroOrFive(value) && value >= 0 && value <= 10) {
                return Promise.resolve()
              }
              return Promise.reject(
                new Error(t('page.statistics_report.label.notify_not_match_grade'))
              )
            }
          }
        ]}
      >
        <InputNumber
          style={{width: '100px', margin: '20px auto 0 auto'}}
          className='hiddenArrow'
          disabled={disabled}
          placeholder={t('page.statistics_report.placeholder.enter_point')}
          onInput={handleChangeInputPoint}
          onBlur={() => setShowTooltip(false)}
        />
      </Form.Item>
    </Tooltip>
  </>
}

export function HistorySession({ data }) {
  const { t } = useTranslation()
  const { activityHistory } = data || {}
  return (
    <>
      <span style={TitleCss}>{t('page.statistics_report.label.history')} </span>
      <Card style={{
        ...cardStyle,
        maxHeight: '400px',
        overflowY: 'scroll',
        scrollbarWidth: 'thin'
      }}>
        {activityHistory?.map((history) => {
          const { time, userId, comment } = history
          const dateTime = moment(Number(time)).format('DD/MM/YYYY')
          const timeHours = moment(Number(time)).format('HH:mm')
          return (
            <div className='m-2'>
              <span>
                {dateTime} {timeHours}
              </span>
              <br />
              <span>
                {userId === 'SYSTEM' || userId === 'SYSTEM_ID' ? (
                  <span>SYSTEM_ID</span>
                ) : (
                  <span>
                    <NameTeacher teacherId={userId} />
                  </span>
                )}: {t(`page.statistics_report.label.${comment}`)}
              </span>
            </div>
          )
        })}
      </Card>
    </>
  )
}

function NameTeacher({ teacherId }) {
  const { data } = useGetWithContext(`GET_TEACHER_BY_ID_${teacherId}`, userApi.detail, teacherId, {
    isDisabled: !teacherId,
  })
  if (!data || !data?.userName) {
    return null
  }
  return <span>{data?.userName}</span>
}

const TagCss = {
  fontSize: '16px',
  padding: '4px 6px',
  backgroundColor: '#fff',
  width: '200px',
  textAlign: 'center',
  color: '#0876B1',
  borderColor: '#0876B1',
  borderRadius: '5px',
  fontWeight: "500"
}

const TitleCss = {
  fontSize: '20px',
  fontWeight: '500',
  margin: '0px 0px 10px 40px'
}

const cardStyle = {
  width: '95%',
  margin: '10px auto',
  borderRadius: '10px', 
  fontSize: '16px' 
}

const titleContentSession = {
  backgroundColor: '#0876B1',
  color: '#fff',
  padding: '8px',
  borderRadius: '20px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}
const tableLessonComment = {
  border: '1px solid #ccc',
  width: '100%',
  borderCollapse: 'collapse',
  padding: '10px',
  textAlign: 'center'
}

const cssBorder = {
  border: '1px solid #ccc',
}